import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const CancellationRefund = () => {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Cancellation & Refund Policy | Aspirant";
  }, []);

  return (
    <div className="bg-[#06010a] text-white font-poppins overflow-x-hidden">
      {/* Hero Section */}
      <section className="flex flex-col justify-center items-center text-center min-h-[60vh] px-4 bg-gradient-to-b from-[#06010a] to-[#260345]">
        <h1 className="text-4xl md:text-6xl font-bold mb-6">
          Cancellation &{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 font-extrabold">
            Refund Policy
          </span>
        </h1>

        <p className="text-lg text-gray-300 max-w-2xl">
          Thank you for choosing Novistep and Aspirant. This policy outlines the terms under 
          which users may cancel subscriptions and request refunds for digital services.
        </p>

        <button
          onClick={() => navigate("/")}
          className="mt-8 bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
        >
          Back to Home
        </button>
      </section>

      {/* Content Section */}
      <section className="bg-white text-black py-20 px-6 md:px-8">
        <div className="max-w-4xl mx-auto space-y-10">
          
          {/* Subscription Plans */}
          <div>
            <h2 className="text-2xl font-bold mb-3">1. Subscription Plans Covered</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our policy applies to the following service tiers offered on job.novistep.com:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Free Plan:</strong> No payment required. Can be discontinued at any time.</li>
              <li><strong>Premium Plan:</strong> Subscription-based AI career guidance, resume building, and interview simulations (e.g., ₹499/month).</li>
            </ul>
          </div>

          {/* Cancellation */}
          <div>
            <h2 className="text-2xl font-bold mb-3">2. Premium Plan Cancellation</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Users may cancel their Premium subscription at any time through the Account Dashboard or by emailing support.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Cancellation stops future billing immediately.</li>
              <li>You will retain access to Premium features until the end of the current billing cycle.</li>
              <li>No partial refunds are provided for the remaining days in a billing cycle.</li>
            </ul>
          </div>

          {/* Refund Policy */}
          <div>
            <h2 className="text-2xl font-bold mb-3">3. Refund Policy & Eligibility</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our AI-powered services are delivered instantly upon activation. Therefore, refunds are generally not provided once the Premium Plan is activated. However, refunds may be considered in the following technical scenarios:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Duplicate payment due to technical error.</li>
              <li>Payment deducted but access not activated within 24 hours.</li>
              <li>Unauthorized transactions reported within 48 hours.</li>
              <li>Proven technical system malfunction that prevents service delivery.</li>
            </ul>
          </div>

          {/* Non-Refundable */}
          <div>
            <h2 className="text-2xl font-bold mb-3">4. Non-Refundable Scenarios</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refunds will not be issued for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Change of mind or personal dissatisfaction with AI-generated outputs.</li>
              <li>Lack of job offers, interview calls, or university admissions.</li>
              <li>Failure to cancel the subscription before the auto-renewal date.</li>
              <li>Violation of our Platform Terms & Conditions.</li>
            </ul>
          </div>

          {/* Process & Timeline */}
          <div>
            <h2 className="text-2xl font-bold mb-3">5. Request Process & Timeline</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To request an eligible refund, email our support team within 48 hours of the transaction with your Registered Email and Transaction ID.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Once approved, refunds are processed within <strong>7–10 business days</strong>. All refunds are credited back to the original payment method via our payment gateway (Razorpay).
            </p>
          </div>

          {/* No Guarantee */}
          <div>
            <h2 className="text-2xl font-bold mb-3">6. No Outcome Guarantee</h2>
            <p className="text-gray-700 leading-relaxed">
              Novistep provides guidance tools. We do not guarantee job placement, admissions, or visa approvals. No refunds will be granted based on career outcomes or external factors beyond our software's technical performance.
            </p>
          </div>

          {/* Governing Law */}
          <div>
            <h2 className="text-2xl font-bold mb-3">7. Dispute Resolution</h2>
            <p className="text-gray-700 leading-relaxed">
              Users are encouraged to contact support before initiating chargebacks. Any disputes regarding payments shall be subject to the exclusive jurisdiction of the courts located in India.
            </p>
          </div>

          {/* Contact Box (Identical Style to Sample) */}
          <div className="bg-gray-50 border rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">Contact Legal Support</h2>
            <p className="text-gray-700 text-sm mb-4">
              If you have any questions regarding cancellations, refund requests, or billing, please reach out to our team.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:info@novistep.com"
                className="inline-block bg-yellow-400 text-black font-semibold px-6 py-2 rounded-lg hover:bg-yellow-500 transition"
              >
                Email Support
              </a>
              <a
                href="https://job.novistep.com"
                target="_blank"
                rel="noreferrer"
                className="inline-block bg-gray-200 text-black font-semibold px-6 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Visit Website
              </a>
            </div>
          </div>
          
          <p className="text-center text-gray-400 text-sm pt-10">
            Last Updated: October 24, 2023 | © {new Date().getFullYear()} Novistep / Aspirant.
          </p>
        </div>  
      </section>
    </div>
  );
};

export default CancellationRefund;